import { memo, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface SwipeOverlayProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  showHint?: boolean;
}

function SwipeOverlayInner({
  currentPage,
  totalPages,
  onPageChange,
  showHint = true,
}: SwipeOverlayProps) {
  const [hintVisible, setHintVisible] = useState(showHint);

  useEffect(() => {
    if (!showHint) return;
    const timer = setTimeout(() => setHintVisible(false), 4000);
    return () => clearTimeout(timer);
  }, [showHint]);

  if (totalPages <= 1) return null;

  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  return (
    <>
      {/* Left edge gradient + chevron */}
      {canGoPrev && (
        <button
          onClick={() => onPageChange(currentPage - 1)}
          className="absolute left-0 top-0 bottom-0 w-10 z-10 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-200"
          style={{
            background: 'linear-gradient(to right, rgba(99,52,94,0.10), rgba(99,52,94,0))',
          }}
          aria-label="Previous page"
        >
          <span className="w-8 h-8 rounded-full bg-white/70 flex items-center justify-center shadow-sm">
            <ChevronLeft size={18} className="text-[#8e4585]" />
          </span>
        </button>
      )}

      {/* Right edge gradient + chevron */}
      {canGoNext && (
        <button
          onClick={() => onPageChange(currentPage + 1)}
          className="absolute right-0 top-0 bottom-0 w-10 z-10 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-200"
          style={{
            background: 'linear-gradient(to left, rgba(99,52,94,0.10), rgba(99,52,94,0))',
          }}
          aria-label="Next page"
        >
          <span className="w-8 h-8 rounded-full bg-white/70 flex items-center justify-center shadow-sm">
            <ChevronRight size={18} className="text-[#8e4585]" />
          </span>
        </button>
      )}

      {/* Swipe hint text (fades out after 4s) */}
      {hintVisible && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
          <span className="px-4 py-1.5 rounded-full bg-[#5a3d58]/10 text-[#8e4585] text-[10px] backdrop-blur-sm animate-pulse">
            Swipe left or right for pages
          </span>
        </div>
      )}
    </>
  );
}

export const SwipeOverlay = memo(SwipeOverlayInner);
